import { computed, reactive, unref, toRefs, type Ref } from 'vue';
import { useStore } from 'vuex';
import { createGlobalState } from '@vueuse/core';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { MESSAGE_ACTION_MODES } from 'components/widgets/WootWriter/constants';

import { Conversation } from '@/types/conversation';
import { Message } from '@/types/message';

export interface RowMessageContext {
  mode: string;
  messageId: number;
}

export interface MessageContext {
  mode: string;
  message: Message;
}

interface MessageContextState {
  contexts: Record<number, RowMessageContext>;
  isInitialized: boolean;
}

const useMessageContextStore = createGlobalState(() => {
  const state = reactive<MessageContextState>({
    contexts: {},
    isInitialized: false,
  });

  const syncToStorage = (
    conversationId: number,
    context: RowMessageContext | null
  ) => {
    if (context) {
      LocalStorage.updateJsonStore(
        LOCAL_STORAGE_KEYS.MESSAGE_CONTEXT,
        conversationId,
        context
      );
    } else {
      LocalStorage.deleteFromJsonStore(
        LOCAL_STORAGE_KEYS.MESSAGE_CONTEXT,
        conversationId
      );
    }
  };

  const actions = {
    init() {
      if (state.isInitialized) return;

      const storageData = LocalStorage.get(LOCAL_STORAGE_KEYS.MESSAGE_CONTEXT);

      if (storageData && typeof storageData === 'object') {
        Object.entries(storageData).forEach(([key, value]) => {
          const id = Number(key);

          if (!Number.isNaN(id) && (value as RowMessageContext)?.mode) {
            state.contexts[id] = value as RowMessageContext;
          }
        });
      }

      state.isInitialized = true;
    },

    setContext(conversationId: number, context: RowMessageContext | null) {
      if (context) {
        state.contexts[conversationId] = context;
      } else {
        delete state.contexts[conversationId];
      }
      syncToStorage(conversationId, context);
    },

    $reset() {
      state.contexts = {};
      state.isInitialized = false;
    },
  };

  return {
    ...toRefs(state),
    actions,
  };
});

export const resetMessageContextStore = () =>
  useMessageContextStore().actions.$reset();

export function useMessageContext(activeChat?: Ref<Conversation | null>) {
  const store = useStore();
  const { contexts, actions: contextActions } = useMessageContextStore();

  contextActions.init();

  const contextRow = computed<RowMessageContext | null>(() => {
    const chat = unref(activeChat);
    if (!chat?.id) return null;
    return contexts.value[chat.id] || null;
  });

  const currentMessageContext = computed<MessageContext | null>(() => {
    const row = contextRow.value;
    const chat = unref(activeChat);

    if (!row || !chat?.messages) return null;

    const message = chat.messages.find(m => m.id === row.messageId);

    if (!message) return null;

    return {
      mode: row.mode,
      message,
    };
  });

  const isEditMode = computed(
    () => currentMessageContext.value?.mode === MESSAGE_ACTION_MODES.EDIT
  );

  const isReplyMode = computed(
    () => currentMessageContext.value?.mode === MESSAGE_ACTION_MODES.REPLY
  );

  const editMessage = (conversationId: number, messageId: number) => {
    contextActions.setContext(conversationId, {
      mode: MESSAGE_ACTION_MODES.EDIT,
      messageId,
    });
  };

  const replyToMessage = (conversationId: number, messageId: number) => {
    contextActions.setContext(conversationId, {
      mode: MESSAGE_ACTION_MODES.REPLY,
      messageId,
    });
  };

  const clearMessageContext = (callback?: () => void) => {
    if (callback) callback();

    const chat = unref(activeChat);
    if (!chat?.id) return;
    contextActions.setContext(chat.id, null);
  };

  const editConfirm = async (
    newMessageContent: string,
    replacedAttachments: any[],
    onEditConfirm?: () => void
  ) => {
    const context = currentMessageContext.value;
    const chat = unref(activeChat);

    if (!context?.message || !chat) return;

    try {
      await store.dispatch('editMessage', {
        conversationId: chat.id,
        editedMessage: context.message,
        content: newMessageContent,
        replacedAttachments,
      });

      clearMessageContext();
      if (onEditConfirm) onEditConfirm();
    } catch (e) {
      throw new Error(`Failed to update message: ${e}`);
    }
  };

  /*
    TODO: Возможно нужно добавить проверку на
    this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
    !this.is360DialogWhatsAppChannel
  */
  const replyConfirm = async (onReplyConfirm?: () => void) => {
    if (onReplyConfirm) onReplyConfirm();
  };

  return {
    currentMessageContext,
    isEditMode,
    isReplyMode,
    editMessage,
    replyToMessage,
    clearMessageContext,
    editConfirm,
    replyConfirm,
  };
}
